// HV Precharge Resistor Calculator: field metadata, defaults, presets and client-side
// per-field validation for instant inline feedback. All the maths (capacitance
// aggregation, RC model, sizing, corners, stresses, chart series) and the authoritative
// validation live in the backend -- see src/lib/toolsApi.js (prechargeToolApi) and
// backend/tools/precharge_tool.py. Keep min/max here in sync with
// backend/core/lookup/precharge_specs.py (tests/test_precharge_tool.py checks it).

export const HV_PRESETS = [
  { value: '400v', label: '400 V class (96s NMC)', values: { packVminV: 300, packVnomV: 360, packVmaxV: 403, battRintMohm: 80, loopRmohm: 5, deltaVV: 20 } },
  { value: '800v', label: '800 V class (192s NMC)', values: { packVminV: 600, packVnomV: 720, packVmaxV: 806, battRintMohm: 160, loopRmohm: 5, deltaVV: 40 } },
  { value: '48v', label: '48 V mild hybrid (13s NMC)', values: { packVminV: 39, packVnomV: 48, packVmaxV: 54.6, battRintMohm: 25, loopRmohm: 2, deltaVV: 2 } },
];

// Indicative values only -- the UI labels them "typical — replace with supplier data".
export const DEFAULT_COMPONENTS = [
  { included: true, name: 'Traction inverter / MCU (front)', qty: 1, xUf: 500, yPosNf: 220, yNegNf: 220, bleedKohm: 100 },
  { included: false, name: 'Traction inverter / MCU (rear, AWD)', qty: 1, xUf: 500, yPosNf: 220, yNegNf: 220, bleedKohm: 100 },
  { included: true, name: 'DC-DC converter (HV→12 V)', qty: 1, xUf: 20, yPosNf: 47, yNegNf: 47, bleedKohm: null },
  { included: true, name: 'On-board charger (OBC)', qty: 1, xUf: 50, yPosNf: 47, yNegNf: 47, bleedKohm: null },
  { included: true, name: 'Electric A/C compressor inverter', qty: 1, xUf: 30, yPosNf: 47, yNegNf: 47, bleedKohm: null },
  { included: true, name: 'HV coolant / PTC heater', qty: 1, xUf: 5, yPosNf: 22, yNegNf: 22, bleedKohm: null },
  { included: true, name: 'PDU / BJB EMI filter', qty: 1, xUf: 2, yPosNf: 100, yNegNf: 100, bleedKohm: null },
  { included: true, name: 'HV harness', qty: 1, xUf: 0, yPosNf: 5, yNegNf: 5, bleedKohm: null },
  { included: false, name: 'DC fast-charge inlet filter (behind its own contactors)', qty: 1, xUf: 5, yPosNf: 100, yNegNf: 100, bleedKohm: null },
];

export const DEFAULT_INPUTS = {
  mode: 'size',
  packVminV: 300,
  packVnomV: 360,
  packVmaxV: 403,
  battRintMohm: 80,
  loopRmohm: 5,
  targetTimeMs: 300,
  resistorOhm: 150,
  eSeries: 'E24',
  criterion: 'percent',
  thresholdPercent: 95,
  deltaVV: 20,
  capTolPercent: 10,
  capAgeingPercent: 0,
  resTolPercent: 5,
  attempts: 3,
  timeoutMargin: 1.5,
  resistorEnergyRatingJ: null,
  resistorVoltageRatingV: null,
  contactorMakeRatingA: null,
};

// "Example" scenario (also the guide's worked example): an 800 V AWD car, both
// inverters on, checking a 220 Ω resistor against optional ratings.
export const EXAMPLE_INPUTS = {
  ...DEFAULT_INPUTS,
  mode: 'check',
  packVminV: 600,
  packVnomV: 720,
  packVmaxV: 806,
  battRintMohm: 160,
  loopRmohm: 5,
  resistorOhm: 220,
  targetTimeMs: 800,
  criterion: 'deltaV',
  deltaVV: 40,
  resistorEnergyRatingJ: 600,
  resistorVoltageRatingV: 1000,
  contactorMakeRatingA: 500,
};
export const EXAMPLE_INCLUDED = { 'Traction inverter / MCU (rear, AWD)': true };

export const E_SERIES_OPTIONS = [
  { value: 'E24', label: 'E24 (±5%)' },
  { value: 'E12', label: 'E12 (±10%)' },
  { value: 'exact', label: 'Exact value' },
];

export const NUMERIC_FIELD_SPECS = [
  { key: 'packVminV', label: 'V min', unit: 'V', min: 10, max: 1500, strictlyPositive: true },
  { key: 'packVnomV', label: 'V nominal', unit: 'V', min: 10, max: 1500, strictlyPositive: true },
  { key: 'packVmaxV', label: 'V max', unit: 'V', min: 10, max: 1500, strictlyPositive: true },
  { key: 'battRintMohm', label: 'Battery Internal Resistance', unit: 'mΩ', min: 0, max: 5000, strictlyPositive: false },
  { key: 'loopRmohm', label: 'Main-Path Loop Resistance', unit: 'mΩ', min: 0, max: 1000, strictlyPositive: false },
  { key: 'targetTimeMs', label: 'Target Precharge Time', unit: 'ms', min: 1, max: 20000, strictlyPositive: true },
  { key: 'resistorOhm', label: 'Precharge Resistor', unit: 'Ω', min: 0.1, max: 100000, strictlyPositive: true },
  { key: 'thresholdPercent', label: 'V_link ≥ x% of V_pack', unit: '%', min: 50, max: 99.9, strictlyPositive: true },
  { key: 'deltaVV', label: 'V_pack − V_link ≤ ΔV', unit: 'V', min: 0.1, max: 500, strictlyPositive: true },
  { key: 'capTolPercent', label: 'Capacitance Tolerance', unit: '±%', min: 0, max: 50, strictlyPositive: false },
  { key: 'capAgeingPercent', label: 'Capacitance Ageing Loss', unit: '−%', min: 0, max: 30, strictlyPositive: false },
  { key: 'resTolPercent', label: 'Resistor Tolerance', unit: '±%', min: 0, max: 20, strictlyPositive: false },
  { key: 'attempts', label: 'Consecutive Attempts', unit: 'N', min: 1, max: 20, strictlyPositive: true, integer: true },
  { key: 'timeoutMargin', label: 'Timeout Margin', unit: '×', min: 1, max: 5, strictlyPositive: true },
  { key: 'resistorEnergyRatingJ', label: 'Resistor Pulse Energy', unit: 'J', min: 0.01, max: 1000000, strictlyPositive: true, optional: true },
  { key: 'resistorVoltageRatingV', label: 'Resistor Voltage Rating', unit: 'V', min: 1, max: 5000, strictlyPositive: true, optional: true },
  { key: 'contactorMakeRatingA', label: 'Main Contactor Make Rating', unit: 'A', min: 1, max: 100000, strictlyPositive: true, optional: true },
];

export const COMPONENT_FIELD_SPECS = [
  { key: 'qty', label: 'Qty', unit: '', min: 1, max: 20, strictlyPositive: true, integer: true },
  { key: 'xUf', label: 'X', unit: 'µF', min: 0, max: 50000, strictlyPositive: false },
  { key: 'yPosNf', label: 'Y+', unit: 'nF', min: 0, max: 20000, strictlyPositive: false },
  { key: 'yNegNf', label: 'Y−', unit: 'nF', min: 0, max: 20000, strictlyPositive: false },
  { key: 'bleedKohm', label: 'Bleed', unit: 'kΩ', min: 0.1, max: 1000000, strictlyPositive: true, optional: true },
];

export const MAX_COMPONENTS = 30;

export function validateField(spec, rawValue) {
  if (rawValue === '' || rawValue === null || rawValue === undefined) {
    return spec.optional ? null : 'Required.';
  }
  const value = Number(rawValue);
  if (Number.isNaN(value) || !Number.isFinite(value)) return 'Must be a number.';
  if (spec.strictlyPositive && value <= 0) return 'Must be greater than 0.';
  if (spec.min !== null && value < spec.min) return `Must be ≥ ${spec.min}.`;
  if (spec.max !== null && value > spec.max) return `Must be ≤ ${spec.max}.`;
  if (spec.integer && !Number.isInteger(value)) return 'Must be a whole number.';
  return null;
}
